/**
 * Which cycles, tracks and sessions each item belongs to — derived, never
 * stored. An item is in a cycle or track through the item pools that hold it,
 * and in a session through the session's item list.
 *
 * One copy, used by the item list (the Cycle, Track and Session filters and
 * columns) and by reports that narrow to one cycle (Pipeline Health, through
 * the shared report range). Two copies would let a report's "Cycle: Q4" count
 * different items from the list it links to.
 */

export interface ItemMembership {
  itemToCycles: Map<string, Set<string>>;
  itemToTracks: Map<string, Set<string>>;
  itemToSessions: Map<string, Set<string>>;
}

export interface MembershipPool {
  priorityIds?: ReadonlyArray<string> | null;
  cycleId?: string | null;
  trackId?: string | null;
}

export interface MembershipSession {
  id: string;
  itemIds?: ReadonlyArray<string> | null;
}

function add(map: Map<string, Set<string>>, key: string, value: string): void {
  const set = map.get(key);
  if (set) set.add(value);
  else map.set(key, new Set([value]));
}

export function buildItemMembership(
  itemPools: ReadonlyArray<MembershipPool> | null | undefined,
  sessions: ReadonlyArray<MembershipSession> | null | undefined,
): ItemMembership {
  const itemToCycles = new Map<string, Set<string>>();
  const itemToTracks = new Map<string, Set<string>>();
  const itemToSessions = new Map<string, Set<string>>();
  for (const pool of itemPools ?? []) {
    for (const itemId of pool.priorityIds ?? []) {
      if (pool.cycleId) add(itemToCycles, itemId, pool.cycleId);
      if (pool.trackId) add(itemToTracks, itemId, pool.trackId);
    }
  }
  for (const session of sessions ?? []) {
    for (const itemId of session.itemIds ?? []) add(itemToSessions, itemId, session.id);
  }
  return { itemToCycles, itemToTracks, itemToSessions };
}
